import { solve, type Constraints } from '@casa/layout';
import { drawGauge, gaugeGeometry, type GaugeValue } from './gauge.ts';
import { drawText, inkHang, layoutText, type Rect, type TextStyle } from './flex_label.ts';
import { drawSparkline, type Sparkline } from './sparkline.ts';
import { drawStyledGauge, type StyledGauge } from './gauge_styles.ts';
import { drawIcon, iconPlacement, type IconDefinition, type IconOptions } from './icon.ts';

/**
 * Tile content as the legacy XAML templates describe it: elements with constraint-layout
 * expressions, laid out in order and drawn on one canvas. Each element reports where it actually
 * draws inside its box (the legacy render bounds), and later elements refer to that.
 */

export type Context = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

export interface LayoutElement {
    constraints: Constraints;
    /** Where the element draws inside `box`, relative to it. Undefined: the whole box. */
    measure?(ctx: Context, box: Rect) : Rect | undefined;
    draw(ctx: Context, box: Rect) : void;
}

/** Lays out `elements` in `area` and draws them. */
export function drawElements(ctx: Context, area: Rect, elements: readonly LayoutElement[]) : void {
    const boxes = solve({ width: area.width, height: area.height }, elements.map((element) => element.constraints), (index, box) => {
        return elements[index]?.measure?.(ctx, box);
    });

    for (const [index, box] of boxes.entries()) {
        const element = elements[index];
        if (!box || !element) {
            continue;
        }

        element.draw(ctx, { left: area.left + box.left, top: area.top + box.top, width: box.width, height: box.height });
    }
}

/** A nested constraint layout (a legacy component view inside a tile template). */
export function groupElement(constraints: Constraints, children: readonly LayoutElement[]) : LayoutElement {
    return {
        constraints,
        draw: (ctx, box) => drawElements(ctx, box, children),
    };
}

/**
 * `element` laid out and measured but not drawn: later elements can refer to its size before
 * the element that shows it is placed (the value tile's unit, measured before its number).
 */
export function measuredOnly(element: LayoutElement) : LayoutElement {
    return { ...element, draw: () => {} };
}

/**
 * Not drawn: as high as `text`'s descenders hang below its baseline, per 100 of its height above it
 * (`inkHang`), so that later elements can leave them room (`<ref>.height/100`).
 */
export function hangElement(constraints: Constraints, text: string, style: TextStyle) : LayoutElement {
    return {
        constraints,
        measure: (ctx, box) => ({ left: 0, top: 0, width: box.width, height: 100 * inkHang(ctx, text, style) }),
        draw: () => {},
    };
}

/** A legacy `SkiaFlexLabel` with text. */
export function textElement(constraints: Constraints, text: string, color: string, style: TextStyle) : LayoutElement {
    return {
        constraints,
        measure: (ctx, box) => layoutText(ctx, text, box.width, box.height, style).bounds,
        draw: (ctx, box) => {
            drawText(ctx, text, box, color, style);
        },
    };
}

/** A legacy `SkiaFlexLabel` with an icon. Draws nothing until the icon is loaded. */
export function iconElement(constraints: Constraints, icon: IconDefinition | undefined, color: string, options: IconOptions = {}) : LayoutElement {
    return {
        constraints,
        measure: (_ctx, box) => (icon ? iconPlacement(icon, box, options) : undefined),
        draw: (ctx, box) => {
            if (icon) {
                drawIcon(ctx, icon, box, color, options);
            }
        },
    };
}

/** A value tile's sparkline (tiles spec §3). */
export function sparklineElement(constraints: Constraints, sparkline: Sparkline) : LayoutElement {
    return {
        constraints,
        draw: (ctx, box) => drawSparkline(ctx, box, sparkline),
    };
}

/** A gauge in one of the styles beside the legacy dial (docs/specs/gauge-styles.md), with its texts. */
export function styledGaugeElement(constraints: Constraints, gauge: StyledGauge) : LayoutElement {
    return {
        constraints,
        draw: (ctx, box) => drawStyledGauge(ctx, box, gauge),
    };
}

/** The legacy `GaugeView`. */
export function gaugeElement(constraints: Constraints, gauge: GaugeValue) : LayoutElement {
    return {
        constraints,
        measure: (_ctx, box) => gaugeGeometry(box.width, box.height).bounds,
        draw: (ctx, box) => drawGauge(ctx, box, gauge),
    };
}
